// BookingPage.js - Complete File (Logic + Component) — JazzCash (real) integration
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AppContext } from '../../../context/AppContext';
import '../../../assets/css/doctor/docbuttons/BookingPage.css';

// ==================== HELPERS & CONSTANTS ====================

const STEPS = [
  { label: 'Select Date', icon: '📅' },
  { label: 'Select Time', icon: '🕐' },
  { label: 'Patient Info', icon: '👤' },
  { label: 'Payment',      icon: '💳' },
];

const formatDateObj = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  const days   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return {
    day:      days[date.getDay()],
    date:     date.getDate(),
    month:    months[date.getMonth()],
    fullDate: date.toDateString(),
  };
};

const formatTimeDisplay = (timeStr) => {
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(h12).padStart(2, '0')}:${m} ${period}`;
};

const validatePatientData = (data) => {
  const errors = {};
  if (!data.name.trim())  errors.name  = 'Name is required';
  if (!data.email.trim()) errors.email = 'Email is required';
  else if (!/\S+@\S+\.\S+/.test(data.email)) errors.email = 'Invalid email address';
  if (!data.phone.trim()) errors.phone  = 'Phone is required';
  if (!data.gender)       errors.gender = 'Gender is required';
  if (!data.age)          errors.age    = 'Age is required';
  return errors;
};

// JazzCash Hosted Checkout ko hidden form ke zariye auto-submit karta hai.
// Ye page ko JazzCash ke sandbox par le jata hai; payment ke baad JazzCash
// hume wapis /payment/return par bhej deta hai.
const submitToJazzCash = (actionUrl, fields) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = actionUrl;

  Object.entries(fields).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

// ==================== COMPONENT ====================

const BookingPage = ({ doctor, onClose }) => {
  const navigate = useNavigate();
  const { baseUrl, user } = useContext(AppContext);
const patientToken = localStorage.getItem('auth_token');
  const [currentStep, setCurrentStep]     = useState(1);
  const [loadingSlots, setLoadingSlots]   = useState(true);
  const [availability, setAvailability]   = useState([]);

  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [selectedSlot, setSelectedSlot]       = useState(null);

  const [patientData, setPatientData]     = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    gender: user?.gender || '',
    age: user?.age || '',
  });
  const [patientErrors, setPatientErrors] = useState({});
  const [isProcessing, setIsProcessing]   = useState(false);

  // ── Fetch real availability ──────────────────────────────────
  useEffect(() => {
    if (!doctor?.id) return;
    setLoadingSlots(true);
    axios.get(`${baseUrl}/patient/Availability/${doctor.id}`, {
      headers: {
        Authorization: `Bearer ${patientToken}`,
        Accept: 'application/json',
      },
    })
      .then((res) => {
        setAvailability(res.data.data || []);
      })
      .catch((err) => {
        console.error('Availability load error:', err);
        Swal.fire({ icon: 'error', title: 'Failed to load', text: 'Could not load available slots for this therapist.' });
      })
      .finally(() => setLoadingSlots(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor?.id, baseUrl]);

  const availableDates = useMemo(() => {
    const datesSet = new Set(availability.map((s) => s.date));
    return Array.from(datesSet).sort();
  }, [availability]);

  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDateStr) return [];
    return availability
      .filter((s) => s.date === selectedDateStr)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [availability, selectedDateStr]);

  const handleInputChange = e =>
    setPatientData({ ...patientData, [e.target.name]: e.target.value });

  const handlePatientNext = () => {
    const errors = validatePatientData(patientData);
    if (Object.keys(errors).length > 0) { setPatientErrors(errors); return; }
    setPatientErrors({});
    setCurrentStep(4);
  };

  // Step 1: appointment ko "payment pending" state mein create karo (slot lock ho jata hai)
  // Step 2: JazzCash checkout initiate karo
  // Step 3: user ko JazzCash sandbox par redirect kar do
  const handlePayWithJazzCash = () => {
    setIsProcessing(true);

    axios.post(`${baseUrl}/patient/Booking/create`, {
      availability_id: selectedSlot.id,
      session_type: 'online',
    }, {
      headers: {
        Authorization: `Bearer ${patientToken}`,
        Accept: 'application/json',
      },
    })
      .then((bookingRes) => {
        const appointmentId = bookingRes.data.data.id;

        return axios.post(`${baseUrl}/patient/Payment/jazzcash/initiate`, {
          appointment_id: appointmentId,
        }, {
          headers: {
            Authorization: `Bearer ${patientToken}`,
            Accept: 'application/json',
          },
        });
      })
      .then((paymentRes) => {
        const { action_url, fields } = paymentRes.data;
        // Yahan se browser JazzCash ke sandbox page par chala jayega
        submitToJazzCash(action_url, fields);
        // Note: is ke baad component unmount ho jayega (page navigate ho raha hai),
        // isliye setIsProcessing(false) yahan call karne ki zaroorat nahi.
      })
      .catch((err) => {
        setIsProcessing(false);
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: err?.response?.data?.message || 'Something went wrong. Please try again.',
        });
      });
  };

  const renderTimeSlot = (slot, i) => (
    <button
      key={i}
      className={`bp-time-slot ${selectedSlot?.id === slot.id ? 'bp-time-selected' : ''}`}
      onClick={() => { setSelectedSlot(slot); setCurrentStep(3); }}
    >
      <span className="bp-time-text">{formatTimeDisplay(slot.start_time)}</span>
    </button>
  );

  return (
    <div className="bp-overlay" onClick={onClose}>
      <div className="bp-container" onClick={e => e.stopPropagation()}>

        {/* Close Button */}
        <button className="bp-close-btn" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Header — profile-style */}
        <div className="bp-header">
          <div className="bp-banner"></div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-60px', paddingBottom: '1rem' }}>
            <div className="bp-img-wrap">
              <img src={doctor.image} alt={doctor.name} className="bp-doctor-img" />
              <div className="bp-verified-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'center', padding: '0 2rem 1rem' }}>
            <h3 style={{ fontSize: '2rem', fontWeight: 800, color: '#FF8C00', marginBottom: '0.4rem' }}>
              {doctor.name}
            </h3>
            <p style={{ fontSize: '1.1rem', color: '#00005a', fontWeight: 700, marginBottom: '0.25rem' }}>
              {doctor.specialty}
            </p>
            <p className="bp-doctor-degree" style={{ margin: '0 0 1.5rem' }}>
              {doctor.degree}
            </p>

            <div className="bp-stats-grid" style={{ maxWidth: '600px', margin: '0 auto' }}>
              {[
                { key: 'experience', icon: '🏆', cls: 'exp', val: doctor.experience },
                { key: 'rating',     icon: '⭐', cls: 'rat', val: doctor.rating },
                { key: 'reviews',    icon: '👥', cls: 'pat', val: doctor.reviews || 0 },
                { key: 'fee',        icon: '💰', cls: 'fee', val: `Rs. ${Number(doctor.fee || 0).toLocaleString()}` },
              ].map(({ key, icon, cls, val }) => (
                <div key={key} className="bp-stat-box">
                  <div className={`bp-stat-icon ${cls}`}>{icon}</div>
                  <div>
                    <span className="bp-stat-value">{val}</span>
                    <span className="bp-stat-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bp-progress">
          {STEPS.map((step, i) => (
            <React.Fragment key={i}>
              <div className={`bp-progress-step ${currentStep === i + 1 ? 'bp-active' : ''} ${currentStep > i + 1 ? 'bp-completed' : ''}`}>
                <div className="bp-step-circle">
                  {currentStep > i + 1 ? '✓' : step.icon}
                </div>
                <span className="bp-step-label">{step.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`bp-progress-line ${currentStep > i + 1 ? 'bp-done' : ''}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="bp-content">

          {/* Step 1 — Date */}
          {currentStep === 1 && (
            <div className="bp-step-content">
              <h2 className="bp-step-title">Select Appointment Date</h2>
              {loadingSlots ? (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>Loading available dates...</p>
              ) : availableDates.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>
                  This therapist has no available slots right now. Please check back later.
                </p>
              ) : (
                <div className="bp-calendar">
                  {availableDates.map((dateStr, i) => {
                    const f = formatDateObj(dateStr);
                    const isSelected = selectedDateStr === dateStr;
                    return (
                      <div
                        key={i}
                        className={`bp-cal-date ${isSelected ? 'bp-selected' : ''}`}
                        onClick={() => { setSelectedDateStr(dateStr); setSelectedSlot(null); setCurrentStep(2); }}
                      >
                        <span className="bp-cal-day">{f.day}</span>
                        <span className="bp-cal-num">{f.date}</span>
                        <span className="bp-cal-month">{f.month}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Time */}
          {currentStep === 2 && (
            <div className="bp-step-content">
              <button className="bp-back-btn" onClick={() => setCurrentStep(1)}>← Back to Date</button>
              <h2 className="bp-step-title">
                Select Time Slot
                {selectedDateStr && <span className="bp-date-display">{formatDateObj(selectedDateStr).fullDate}</span>}
              </h2>
              {slotsForSelectedDate.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>No slots available for this date.</p>
              ) : (
                <div className="bp-time-period">
                  <div className="bp-time-grid">{slotsForSelectedDate.map(renderTimeSlot)}</div>
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Patient Info */}
          {currentStep === 3 && (
            <div className="bp-step-content">
              <button className="bp-back-btn" onClick={() => setCurrentStep(2)}>← Back to Time</button>
              <h2 className="bp-step-title">Patient Information</h2>

              <div className="bp-summary">
                {[
                  { icon: '📅', label: 'Date',   val: selectedDateStr && formatDateObj(selectedDateStr).fullDate },
                  { icon: '🕐', label: 'Time',   val: selectedSlot && formatTimeDisplay(selectedSlot.start_time) },
                  { icon: '👨‍⚕️', label: 'Doctor', val: doctor.name },
                ].map((item, i) => (
                  <div key={i} className="bp-summary-item">
                    <span className="bp-summary-icon">{item.icon}</span>
                    <div>
                      <span className="bp-summary-label">{item.label}</span>
                      <span className="bp-summary-val">{item.val}</span>
                    </div>
                  </div>
                ))}
              </div>

              <form className="bp-form" onSubmit={e => { e.preventDefault(); handlePatientNext(); }}>
                <div className="bp-form-row">
                  {['name', 'email', 'phone'].map(field => (
                    <div key={field} className="bp-form-group">
                      <label>{field.charAt(0).toUpperCase() + field.slice(1)} *</label>
                      <input
                        type={field === 'email' ? 'email' : 'text'}
                        name={field} value={patientData[field]}
                        onChange={handleInputChange}
                        placeholder={`Enter your ${field}`}
                        className={patientErrors[field] ? 'bp-error' : ''}
                      />
                      {patientErrors[field] && <span className="bp-field-error">{patientErrors[field]}</span>}
                    </div>
                  ))}
                </div>
                <div className="bp-form-row">
                  <div className="bp-form-group">
                    <label>Gender *</label>
                    <select name="gender" value={patientData.gender} onChange={handleInputChange} className={patientErrors.gender ? 'bp-error' : ''}>
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {patientErrors.gender && <span className="bp-field-error">{patientErrors.gender}</span>}
                  </div>
                  <div className="bp-form-group">
                    <label>Age *</label>
                    <input
                      type="number" name="age" value={patientData.age}
                      onChange={handleInputChange} placeholder="Enter your age"
                      className={patientErrors.age ? 'bp-error' : ''}
                    />
                    {patientErrors.age && <span className="bp-field-error">{patientErrors.age}</span>}
                  </div>
                </div>
                <button type="submit" className="bp-next-btn">Proceed to Payment →</button>
              </form>
            </div>
          )}

          {/* Step 4 — Payment (JazzCash only) */}
          {currentStep === 4 && (
            <div className="bp-step-content">
              <button className="bp-back-btn" onClick={() => setCurrentStep(3)} disabled={isProcessing}>← Back to Info</button>
              <h2 className="bp-step-title">Payment</h2>

              <div className="bp-fee-box">
                <div>
                  <div className="bp-fee-label">Consultation Fee</div>
                  <div className="bp-fee-amount">Rs. {Number(doctor.fee || 0).toLocaleString()}</div>
                  <div className="bp-fee-note">One-time appointment fee</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.3rem' }}>Booking For</div>
                  <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>{patientData.name}</div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                    {selectedDateStr && formatDateObj(selectedDateStr).fullDate} • {selectedSlot && formatTimeDisplay(selectedSlot.start_time)}
                  </div>
                </div>
              </div>

              <div className="bp-card-details" style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📱</div>
                <div className="bp-card-details-title" style={{ marginBottom: '0.3rem' }}>Pay with JazzCash</div>
                <p style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '1.5rem' }}>
                  Aap ko JazzCash ke secure page par le jaya jayega. Wahan apna mobile
                  wallet number aur PIN/OTP se payment complete karein.
                </p>

                <button
                  className="bp-next-btn"
                  onClick={handlePayWithJazzCash}
                  disabled={isProcessing}
                  style={{ opacity: isProcessing ? 0.85 : 1 }}
                >
                  {isProcessing ? (
                    <>
                      <span style={{ width: 20, height: 20, border: '3px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', animation: 'bp-spin 0.8s linear infinite', display: 'inline-block' }}></span>
                      Redirecting to JazzCash...
                    </>
                  ) : '🔒 Pay with JazzCash'}
                </button>
              </div>

              <div className="bp-secure-badge">Secured by JazzCash — Slot reserved for 10 minutes</div>
              <style>{`@keyframes bp-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default BookingPage;